import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography
} from '@mui/material';
import axiosInstance from '../services/axiosInstance';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-CH') : '—';

export default function SignPage() {
    const { token } = useParams();

    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);

    const [pin, setPin] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState(null);
    const [signingUrl, setSigningUrl] = useState(null);

    useEffect(() => {
        axiosInstance.get(`/api/public/sign/${token}`)
            .then(r => setInfo(r.data))
            .catch(() => setError('Dieser Link ist ungültig oder abgelaufen.'))
            .finally(() => setLoading(false));
    }, [token]);

    const handleVerify = async () => {
        setVerifying(true);
        setVerifyError(null);
        try {
            const r = await axiosInstance.post(`/api/public/sign/${token}/verify`, { pin });
            setSigningUrl(r.data.signingUrl);
        } catch {
            setVerifyError('Ungültiger PIN. Bitte versuchen Sie es erneut.');
        } finally {
            setVerifying(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: '#f0f4f7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
        }}>
            <Paper elevation={3} sx={{ maxWidth: 480, width: '100%', p: 4, borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: '#427898' }}>
                    Angebot unterzeichnen
                </Typography>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                {!loading && !error && info && (
                    <>
                        <Stack spacing={1} sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">Angebot</Typography>
                            <Typography variant="body1" fontWeight={600}>{info.quoteDisplayName}</Typography>
                            <Typography variant="body2" color="text.secondary">Kunde</Typography>
                            <Typography variant="body1">{info.customerName}</Typography>
                            <Typography variant="body2" color="text.secondary">Gültig bis</Typography>
                            <Typography variant="body1">{fmtDate(info.expiresAt)}</Typography>
                        </Stack>

                        {info.status === 'SIGNED' && (
                            <Alert severity="success">Dieses Angebot wurde bereits unterzeichnet.</Alert>
                        )}

                        {info.status === 'DECLINED' && (
                            <Alert severity="error">Dieses Angebot wurde abgelehnt.</Alert>
                        )}

                        {info.status === 'PENDING' && !signingUrl && (
                            <>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Bitte geben Sie den PIN ein, den Sie per E-Mail erhalten haben, um das Angebot zu unterzeichnen.
                                </Typography>
                                <Stack spacing={2}>
                                    <TextField
                                        label="PIN"
                                        value={pin}
                                        onChange={e => setPin(e.target.value)}
                                        inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                                        variant="outlined"
                                        fullWidth
                                        onKeyDown={e => { if (e.key === 'Enter') handleVerify(); }}
                                    />
                                    {verifyError && <Alert severity="error">{verifyError}</Alert>}
                                    <Button
                                        variant="contained"
                                        sx={{ bgcolor: '#427898' }}
                                        onClick={handleVerify}
                                        disabled={verifying || pin.length < 4}
                                        fullWidth
                                    >
                                        {verifying ? <CircularProgress size={20} color="inherit" /> : 'PIN bestätigen'}
                                    </Button>
                                </Stack>
                            </>
                        )}

                        {signingUrl && (
                            <Stack spacing={2} alignItems="center">
                                <Alert severity="success" sx={{ width: '100%' }}>
                                    PIN bestätigt. Sie können das Angebot jetzt elektronisch unterzeichnen.
                                </Alert>
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{ bgcolor: '#427898', px: 4 }}
                                    href={signingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Jetzt unterzeichnen
                                </Button>
                            </Stack>
                        )}
                    </>
                )}
            </Paper>
        </Box>
    );
}
